/**
 * Application Color Palette
 * Centralized color definitions for consistent theming across the application
 */
export default {
	// ============================================
	// Brand Colors
	// ============================================
	/** Primary brand color - Used for buttons, headers, key UI elements */
	primary: "#9c343e",

	/** Primary hover state - Darker shade for interactive elements */
	darkCrimsonRed: "#ba1f2eff",

	/** Primary with low opacity - Used for subtle backgrounds and overlays */
	darkRose: "#9c343e22",

	/** Primary with medium opacity - Used for disabled states and overlays */
	mutedCrimson: "#9c343e4d",

	/** Primary faint shadow - Used for subtle shadows on primary elements */
	primaryFaintShadow: "#9C343E26",

	// ============================================
	// Base Colors
	// ============================================
	/** Pure black */
	black: "#000000",

	/** Pure white */
	white: "#ffffff",

	// ============================================
	// Semantic Colors
	// ============================================
	/** Error/Alert color */
	red: "#FF0000",

	/** Warning/Alert color */
	orange: "#ef5350",

	/** Success/Confirmation color */
	green: "#4caf50",

	/** Dark success variant */
	darkGreen: "#1E4620",

	/** Dark error variant */
	darkRed: "#5F2120",

	/** Error emphasis */
	crimsonRed: "#C62828",

	/** Success emphasis - Used for filled/active states */
	brightGreen: "#40ff00ff",

	/** Highlight color - Used for selected items in dark backgrounds */
	lightSkyBlue: "#aacbff",

	/** Accent pink - Used for hover states and highlights */
	blushPink: "#fbe9ea",

	// ============================================
	// Neutral Grays (Light to Dark)
	// ============================================
	/** Very light gray - Background for sections */
	veryLightGray: "#f2f2f2",

	/** Pale gray - Modal/dialog backgrounds */
	paleGray: "#fafafa",

	/** Very pale gray - Subtle section backgrounds */
	veryPaleGray: "#f9f9f9",

	/** White smoke - Card headers, subtle backgrounds */
	whiteSmoke: "#f5f5f5",

	/** Near white - Button backgrounds */
	nearWhite: "#f0f0f0",

	/** Warm off-white - Hover states for light buttons */
	warmOffWhite: "#eae9e9ff",

	/** Light gray - Borders, dividers */
	lightGray: "#eeeeee",

	/** Light gray border - Modal borders */
	lightGrayBorder: "#e0e0e0",

	/** Gainsboro-like - Strong borders */
	gainsboroLike: "#dedede",

	/** Light silver - Table row alternating color */
	lightSilver: "#dfdbdb",

	/** Silver gray - Progress bar backgrounds */
	silverGray: "#cecece",

	/** Soft silver - Table borders in dark themes */
	softSilver: "#bababaff",

	/** Dusty gray - Borders on cards */
	dustyGray: "#bab3b3ff",

	/** Ash gray - Panel backgrounds */
	ashGray: "#aeaeaeff",

	/** Neutral gray - Shadows, secondary text */
	neutralGray: "#888888",

	/** Medium dark gray - Progress bars */
	mediumDarkGray: "#777777",

	/** Medium gray - Subtext */
	mediumGray: "#666666",

	/** Neutral dark gray - Text, labels */
	neutralDarkGray: "#555555",

	/** Dark warm gray - Button hover states */
	darkWarmGray: "#544545ff",

	/** Charcoal gray - Primary text, UI elements */
	charcoalGray: "#444444",

	/** Charcoal tint - Subtle overlays */
	charcoalTint: "#44444411",

	/** Warm charcoal - Secondary text */
	warmCharcoal: "#302f2fff",

	/** Dark charcoal - Dark theme backgrounds */
	darkCharcoal: "#262626",

	/** Gray - Special background color */
	gray: "#f7f7ed",

	// ============================================
	// Shadows & Overlays
	// ============================================
	/** Subtle shadow - Light cards (12% opacity) */
	subtleShadow: "#00000012",

	/** Subtle shadow light - Slightly more visible (14% opacity) */
	subtleShadowLight: "#00000014",

	/** Soft shadow alpha - Navigation shadows (1a) */
	softShadowAlpha: "#0000001a",

	/** Soft black overlay - Card hover shadows (21% opacity) */
	softBlackOverlay: "#00000021",

	/** Medium shadow alpha - Navigation shadows (1F) */
	mediumShadowAlpha2: "#0000001F",

	/** Medium shadow alpha - Navigation shadows (24% opacity) */
	mediumShadowAlpha: "#00000024",

	/** Dark shadow alpha - Card hover emphasis (26% opacity) */
	darkShadowAlpha: "#00000026",

	/** Shadow alpha 33 - Paper shadows (33% opacity) */
	shadowAlpha33: "#00000033",

	/** Dark shadow alpha 99 - Mask gradients (99% opacity) */
	darkShadowAlpha99: "#00000099",

	/** Full black alpha - Mask gradients (full opacity) */
	fullBlackAlpha: "#000000FF",

	/** White alpha - Hover borders and highlights (CC opacity) */
	whiteAlpha: "#ffffffCC",
};
